import { AICapabilityContext } from 'core/hooks/useIsAIEnabled';
import { clearChatbotStorage } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import { isNewDaySinceLastActive, stampActiveDay } from '../constants/storageKeys';
import { useAudioManager } from '../hooks/useAudioManager';
import AIChatAgentConfigProvider from './AIChatAgentConfig/AIChatAgentConfigProvider';
import type { AgentConfigState } from './AIChatAgentConfig/aiChatAgentConfigTypes';
import AIChatConversationProvider from './AIChatConversation/AIChatConversationProvider';
import { CONVERSATION_MODES, type ConversationMode } from '../constants/modes';
import { detectConversationMode } from './AIChatConversation/aiChatConversationReducer';
import type { ConversationState } from './AIChatConversation/aiChatConversationTypes';
import { useAIChatConversation } from './AIChatConversation/useAIChatConversation';
import AIChatPracticeSessionProvider from './AIChatPracticeSession/AIChatPracticeSessionProvider';
import type { PracticeSessionState } from './AIChatPracticeSession/aiChatPracticeSessionTypes';
import AIChatShellProvider from './AIChatShell/AIChatShellProvider';
import { AI_CHAT_DEFAULT_WIDTH, detectIsDevMode } from './AIChatShell/aiChatShellReducer';
import type { ShellState } from './AIChatShell/aiChatShellTypes';
import { useAIChatShell } from './AIChatShell/useAIChatShell';
import { loadPersistedUIState, useAIChatUiStatePersistence } from './useAIChatUiStatePersistence';

/**
 * The shared audio-manager instance produced by useAudioManager().
 */
type AudioManager = ReturnType<typeof useAudioManager>;

// detectConversationMode / detectIsDevMode are owned by their slice reducers (single source of truth)
// and imported above; the composition root seeds AIChatShellProvider / the Conversation slice
// with the same values those reducers use as fallbacks.

type ResolvedInitialChatbotState = {
  shell: {
    isChatbotOpen: boolean;
    chatbotWidth: number;
    isDevMode: boolean;
  };
  conversation: {
    currentConversationId: string | null;
    mode: ConversationMode;
  };
};

/**
 * Resolve the persisted-UI + new-day initialization ONCE at the composition root.
 *
 * Performs the side effects (clearChatbotStorage on a new day + stamping the active day) a
 * single time, then returns the resolved Shell slice (isChatbotOpen/chatbotWidth/isDevMode) and
 * the Conversation seeds it feeds (currentConversationId gated on study-mode + not-new-day, plus
 * the URL-derived mode). The Shell fields flow into AIChatShellProvider; the rest into the
 * Conversation provider.
 */
const resolveInitialChatbotState = (): ResolvedInitialChatbotState => {
  const persistedUI = loadPersistedUIState();
  // Always determine initial mode based on current URL (never persist mode)
  const initialMode = detectConversationMode(window.location.pathname);

  // On a new day, clear persisted conversation state to start fresh
  const isNewDay = isNewDaySinceLastActive();
  if (isNewDay) {
    clearChatbotStorage();
  }
  stampActiveDay();

  return {
    shell: {
      isChatbotOpen: isNewDay ? false : persistedUI.isChatbotOpen || false,
      chatbotWidth: isNewDay ? AI_CHAT_DEFAULT_WIDTH : persistedUI.chatbotWidth || AI_CHAT_DEFAULT_WIDTH,
      isDevMode: detectIsDevMode(),
    },
    conversation: {
      // Only load persisted conversationId if in study mode and NOT a new day. Normalize a missing
      // persisted value to null (never undefined): the Conversation slice seeds this through a
      // `{ ...defaults, ...overrides }` spread, and an explicit `undefined` override would overwrite
      // the null default — breaking useConversationEngine's `previousId === null` cold-start guard.
      currentConversationId: initialMode === CONVERSATION_MODES.STUDY && !isNewDay ? persistedUI.currentConversationId ?? null : null,
      mode: initialMode, // Always derived from URL, never persisted
    },
  };
};

/**
 * Single-purpose context carrying the shared audio-manager instance (or null when AI is disabled /
 * no AIChatProvider is mounted). A dedicated context keeps useAudioManager a single shared
 * instance across every ChatMessage row — the singleton that enforces "only one audio plays at a
 * time" — while letting read-only message display degrade to null.
 */
const AIChatAudioContext = createContext<AudioManager | null>(null);

type AIChatEffectsCoordinatorProps = {
  children: React.ReactNode;
  isAIEnabled?: boolean;
};

/**
 * Effects-only COORDINATOR. Owns NO reducer and no merged value. Its scope is the cross-cutting
 * concerns no single slice owns: UI-state persistence + cross-tab sync (a composite payload
 * spanning Shell and Conversation, see useAIChatUiStatePersistence) and the shared audio-manager
 * instance (provided through AIChatAudioContext, null when AI is disabled). Slice-owned effects
 * live with their slice (e.g. the Conversation lifecycle resets in useConversationLifecycleResets).
 * It renders {children} with NO merged Provider; every consumer reads the slice contexts directly.
 */
function AIChatEffectsCoordinator({ children, isAIEnabled = true }: AIChatEffectsCoordinatorProps): JSX.Element {
  const { mode, currentConversationId, setCurrentConversationId } = useAIChatConversation();
  const { isChatbotOpen, chatbotWidth, openChatbot, closeChatbot, setChatbotWidth } = useAIChatShell();

  // IMPORTANT: Hooks MUST be called unconditionally (React rules of hooks). The single shared
  // audioManager instance is created here and gated to null when AI is disabled.
  const audioManager = useAudioManager();

  useAIChatUiStatePersistence({
    chatbotWidth,
    closeChatbot,
    currentConversationId,
    isAIEnabled,
    isChatbotOpen,
    mode,
    openChatbot,
    setChatbotWidth,
    setCurrentConversationId,
  });

  return <AIChatAudioContext.Provider value={isAIEnabled ? audioManager : null}>{children}</AIChatAudioContext.Provider>;
}

AIChatEffectsCoordinator.propTypes = {
  children: PropTypes.node.isRequired,
  isAIEnabled: PropTypes.bool,
};

/**
 * Per-slice seeding for AIChatProvider. Each key is handed to its slice provider as
 * initial-state overrides on top of that slice's defaults (and, for shell/conversation, on top
 * of the persisted-UI resolution).
 */
export type AIChatInitialState = {
  shell?: Partial<ShellState>;
  agentConfig?: Partial<AgentConfigState>;
  practiceSession?: Partial<PracticeSessionState>;
  conversation?: Partial<ConversationState>;
};

type AIChatProviderProps = {
  children: React.ReactNode;
  initialState?: AIChatInitialState | null;
  isAIEnabled?: boolean;
};

/**
 * Composition root. Resolves the persisted-UI + new-day initialization ONCE (side effects run a
 * single time), then nests the four slice providers around the effects-only coordinator, all
 * wrapped by the outermost AICapability seam:
 *   AICapabilityContext > AIChatShellProvider > AIChatAgentConfigProvider >
 *   AIChatPracticeSessionProvider > AIChatConversationProvider > AIChatEffectsCoordinator > children.
 */
export function AIChatProvider({ children, initialState = {}, isAIEnabled = true }: AIChatProviderProps): JSX.Element {
  // The `= {}` default only fills `undefined`; ChatbotWidget's defaultProps pass `initialState`
  // as `null` (React substitutes defaults for `undefined` only), so normalize null → {}.
  const { agentConfig, conversation, practiceSession, shell } = initialState ?? {};

  // Run persistedUI + isNewDay resolution (and its side effects) exactly once per mount.
  const resolvedRef = useRef<ResolvedInitialChatbotState | null>(null);
  if (resolvedRef.current === null) {
    resolvedRef.current = resolveInitialChatbotState();
  }
  const resolved = resolvedRef.current;

  // Resolved values are the seed; explicit initialState overrides win on top.
  const shellSeed = { ...resolved.shell, ...shell };
  const conversationSeed = { ...resolved.conversation, ...conversation };

  // Memoize the AICapability value so a re-render with an unchanged isAIEnabled does not hand a
  // fresh object to every useIsAIEnabled() consumer (which would re-render them all needlessly).
  const aiCapabilityValue = useMemo(() => ({ isAIEnabled }), [isAIEnabled]);

  // areActionsEnabled={isAIEnabled}: when AI is disabled every slice exposes a gated no-op action
  // set (state stays readable). The slices stay generic: they know only the boolean, not the AI
  // capability.
  return (
    <AICapabilityContext.Provider value={aiCapabilityValue}>
      <AIChatShellProvider areActionsEnabled={isAIEnabled} initialState={shellSeed}>
        <AIChatAgentConfigProvider areActionsEnabled={isAIEnabled} initialState={agentConfig}>
          <AIChatPracticeSessionProvider areActionsEnabled={isAIEnabled} initialState={practiceSession}>
            <AIChatConversationProvider areActionsEnabled={isAIEnabled} initialState={conversationSeed}>
              <AIChatEffectsCoordinator isAIEnabled={isAIEnabled}>{children}</AIChatEffectsCoordinator>
            </AIChatConversationProvider>
          </AIChatPracticeSessionProvider>
        </AIChatAgentConfigProvider>
      </AIChatShellProvider>
    </AICapabilityContext.Provider>
  );
}

AIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialState: PropTypes.shape({
    agentConfig: PropTypes.object,
    conversation: PropTypes.object,
    practiceSession: PropTypes.object,
    shell: PropTypes.object,
  }),
  isAIEnabled: PropTypes.bool,
};

/**
 * Nullable accessor for the shared audio-manager instance. Returns the AudioManager when a
 * AIChatProvider is mounted with AI enabled, or `null` otherwise (AI disabled, or no provider —
 * e.g. read-only message display). Never throws.
 */
export function useAudioManagerIfPresent(): AudioManager | null {
  return useContext(AIChatAudioContext);
}
